import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { apiFetch } from "../../services/api";
import { colors } from "../../theme";

export default function SpotifyRedirect() {
    const location = useLocation();
    const navigate = useNavigate();
    const hasProcessedCode = useRef(false);

    function getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()!.split(';').shift()!;
        }
        return null;
    }

    function deleteCookie(name: string): void {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }

    async function getAccessToken(code: string) {
        console.log("Getting access token for code:", code.substring(0, 10) + "...");

        return apiFetch('/v1/spotify/accessToken', {
            method: 'POST',
            body: JSON.stringify({
                'code': code
            })
        }).then((response) => response.json())
        .then(data => {
            // Backend now returns expiresAt (epoch ms) alongside the token - size the
            // cookie's lifetime to match instead of a fixed, wrong-most-of-the-time 7 days.
            const maxAgeSeconds = data.expiresAt
                ? Math.max(0, Math.floor((data.expiresAt - Date.now()) / 1000))
                : 60 * 60; // fallback: Spotify access tokens normally live 1 hour
            document.cookie = `spotifyAuthToken=${data.spotifyAccessToken}; path=/; max-age=${maxAgeSeconds}`;
            console.log("Access token stored successfully");
            return true;
        });
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get("code");

        if (code && !hasProcessedCode.current) {
            hasProcessedCode.current = true;
            console.log("Processing Spotify authorization code");

            sessionStorage.setItem("spotifyAuthCode", code);
            document.cookie = `spotifyAuthCode=${code}; path=/; max-age=${60 * 60 * 24 * 7}`;

            const lastPage = getCookie("lastPage");

            getAccessToken(code).then(() => {
                // Clean up cookies after successful token exchange
                deleteCookie("lastPage");
                deleteCookie("spotifyAuthCode");

                if (lastPage === "settings") {
                    navigate("/dashboard/settings");
                } else {
                    navigate("/dashboard");
                }
            }).catch((error) => {
                console.error("Token exchange failed:", error);
                if(lastPage === "settings"){
                    navigate("/dashboard/settings?error=spotify_auth_failed");
                }else{
                    navigate("/dashboard?error=spotify_auth_failed");
                }
            });
        }
    }, [location, navigate]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                px: 3,
                textAlign: "center",
                background: `radial-gradient(900px 420px at 50% 0%, rgba(29,155,240,0.28) 0%, ${colors.base} 70%)`,
            }}
        >
            <Box
                component="img"
                src="/assets/Shufl.png"
                alt="Shufl"
                sx={{ height: 56, width: "auto" }}
            />
            <CircularProgress sx={{ color: colors.accent }} size={30} thickness={5} />
            <Box>
                <Typography variant="h4" sx={{ mb: 1 }}>Connecting Spotify…</Typography>
                <Typography variant="body2">Hang tight, we're finishing the handshake.</Typography>
            </Box>
        </Box>
    );
}